import React, { useCallback, useEffect, useState } from 'react';
import FlagLabel from '../FlagLabel';
import Expert from '../Unit/Expert';
import {
  Agent,
  MappedAgent,
  StrapiAgent,
} from '../../apolloClient/types/Agents';
import { mapAgent } from '../../src/utils/agentUtils';
import Link from 'next/link';
import { useAuth } from 'components/Auth/AuthProvider';
import {
  isNotEmpty,
  validateEmail,
  getLeadIdFromLocalStorage,
  saveLeadIdToLocalStorage,
  getbrowserUUIDFromLocalStorage,
} from 'src/utils/utils';
import { useRouter } from 'next/router';
import { SEND_CONTACT_US_FORM } from 'apolloClient/mutations/alerts';
import { useMutation } from '@apollo/client';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { getContactFormContextData } from 'src/utils/buildingUtils';
import PopUpThanks from 'components/PopUpThanks';
import Agents from 'components/Agents';

interface ContactUsProps {
  agent: StrapiAgent;
  experts?: Agent[];
  imageSizes?: string;
  flagSizes?: string;
  customGap?: string;
  infoBlockClasses?: string;
  expertNameFonts?: string;
  buttonClasses?: string;
  showSecondAgent?: boolean;
}

const ContactUs: React.FC<ContactUsProps> = ({
  agent,
  experts,
  imageSizes,
  flagSizes,
  customGap,
  infoBlockClasses,
  expertNameFonts,
  buttonClasses,
  showSecondAgent,
}) => {
  const { executeRecaptcha } = useGoogleReCaptcha();

  const { email, lastName, firstName, image } = mapAgent(agent) as MappedAgent;

  const router = useRouter();
  const { me } = useAuth();
  const [fieldsValid, setFieldsValid] = useState({
    Name: true,
    Email: true,
    Phone: true,
    Comments: true,
  });

  const [contactFormData, setContactFormData] = useState({
    Name: '',
    Phone: '',
    Email: '',
    Comments: '',
    FormLoaded6tY4bPYk: '',
    PhoneNumber6tY4bPYk: '',
    submitState: {
      isSubmitted: false,
      isTouched: false,
      isHeaderFormActive: false,
      submitting: false,
    },
  });

  const [sendContactUsForm] = useMutation(SEND_CONTACT_US_FORM);

  useEffect(() => {
    setContactFormData((prevData) => ({
      ...prevData,
      Name: me?.fullName ?? '',
      Phone: me?.phoneNumber ?? '',
      Email: me?.email ?? '',
      FormLoaded6tY4bPYk: Date.now().toString(),
      agent: localStorage.getItem('agent') || router.query.agent?.toString(),
      leadId: localStorage.getItem('leadId'),
    }));
  }, [me]);

  const validateFields = () => {
    let nameValid = isNotEmpty(contactFormData.Name);
    let commentsValid = isNotEmpty(contactFormData.Comments);
    let emailValid = validateEmail(contactFormData.Email);
    let phoneValid = isNotEmpty(contactFormData.Phone);

    setContactFormData((prevState) => ({
      ...prevState,
      submitState: { ...prevState.submitState, isTouched: true },
    }));

    setFieldsValid({
      Name: nameValid,
      Comments: commentsValid,
      Email: emailValid,
      Phone: phoneValid,
    });

    return nameValid && commentsValid && emailValid && phoneValid;
  };

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setContactFormData((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));

    if (contactFormData.submitState.isTouched) {
      if (e.target.name === 'Name' || e.target.name === 'Comments') {
        let valid = isNotEmpty(e.target.value);
        setFieldsValid((prev) => ({ ...prev, [e.target.name]: valid }));
      } else if (e.target.name === 'Email') {
        let validEmail = validateEmail(e.target.value);
        setFieldsValid((prev) => ({ ...prev, Email: validEmail }));
      } else if (e.target.name === 'Phone') {
        let validPhone = isNotEmpty(e.target.value);
        setFieldsValid((prev) => ({ ...prev, Phone: validPhone }));
      }
    }
  };

  const eventContactUs = () => {
    window.gtag('event', 'contact_us', {
      event_category: 'contact_us',
      event_label: 'Success',
      value: 1,
    });
  };

  const onContactFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    eventContactUs();
    const token = (await onVerify()) || '';
    if (validateFields()) {
      let payload = {
        postData: {
          ...contactFormData,
          ...getContactFormContextData(
            undefined,
            undefined,
            undefined,
            undefined,
            router.asPath
          ),
        },
        defaultMessage: 'Default message',
        isHeaderForm: false,
      };

      const checkForRent = () => {
        if (router.query.hasOwnProperty('forRent')) {
          return router.query['forRent'] == 'true' ? 'RENT' : '';
        }
        return '';
      };

      setContactFormData((formData) => ({
        ...formData,
        submitState: { ...formData.submitState, submitting: true },
      }));

      const browserUUID = getbrowserUUIDFromLocalStorage() || undefined;

      sendContactUsForm({
        variables: {
          ...payload.postData,
          browserUUID,
          RecaptchaToken: token,
        },
      }).then((data) => {
        try {
          const oldLeadId = getLeadIdFromLocalStorage() || null;
          const newLeadId = data?.data?.sendContactUsForm?.leadId;

          if ((!oldLeadId && newLeadId) || oldLeadId !== newLeadId) {
            saveLeadIdToLocalStorage(newLeadId);
          }
        } catch (error) {
          console.error(error);
        }
        setContactFormData((formData) => ({
          ...formData,
          submitState: {
            ...formData.submitState,
            isSubmitted: true,
            submitting: false,
          },
        }));
      });
    }
  };

  const onVerify = useCallback(async () => {
    if (!executeRecaptcha) return;

    const token = await executeRecaptcha('contact_us');
    return token;
  }, [executeRecaptcha]);

  const onClosingPopup = () => {
    setContactFormData((prevState) => ({
      ...prevState,
      submitState: { ...prevState.submitState, isSubmitted: false },
    }));
  };

  return (
    <>
      <div
        className={`flex flex-col md:flex-row xl:flex-col md:gap-[80px] lg:gap-[12px] ${customGap}`}
      >
        <div className={`md:w-1/2 xl:w-[414px]`}>
          <div className={`w-3/4 xl:w-2/3 ${flagSizes}`}>
            <FlagLabel name="Contact us" />
          </div>
          <div>
            <div className={`flex pt-2 my-8 xl:pt-0 ${infoBlockClasses}`}>
              <Agents
                experts={experts}
                containerClassName="md:w-[84px] mr-[23px]"
                textClassName="w-[156px]"
                containerExpert="md:!mt-0 md:!mb-[40px] "
                showSecondAgent={showSecondAgent}
              />
            </div>
          </div>
        </div>
        <form
          className="flex flex-col text-16-26-0.3 md:mt-20 xl:mt-0 w-full lg:w-[414px]"
          onSubmit={onContactFormSubmit}
        >
          <input
            name="Name"
            className="px-4 py-2 text-16-26-0.3 border border-gray"
            type="text"
            value={contactFormData.Name}
            onChange={handleChange}
            placeholder="Your Name"
          />
          {contactFormData.submitState.isTouched && !fieldsValid.Name && (
            <span className="field-validation-error">
              <span className="pl-3 text-red">Name is required</span>
            </span>
          )}
          <input
            name="Phone"
            className={`px-4 py-2 text-16-26-0.3 border-gray border-r border-l ${
              !fieldsValid.Name ? 'border-t' : ''
            } ${!fieldsValid.Phone ? 'border-b' : ''}`}
            type="phone"
            value={contactFormData.Phone}
            onChange={handleChange}
            placeholder="Phone"
          />
          {contactFormData.submitState.isTouched && !fieldsValid.Phone && (
            <span className="field-validation-error">
              <span className="pl-3 text-red">Phone is invalid</span>
            </span>
          )}
          <input
            name="Email"
            className="px-4 py-2 text-16-26-0.3 border border-gray"
            type="email"
            value={contactFormData.Email}
            onChange={handleChange}
            placeholder="Email"
          />
          {contactFormData.submitState.isTouched && !fieldsValid.Email && (
            <div className="field-validation-error">
              <div className="pl-3 text-red">Email address is invalid</div>
            </div>
          )}
          <textarea
            className="px-4 py-2 mt-4 border border-gray"
            name="Comments"
            id=""
            cols={10}
            rows={3}
            value={contactFormData.Comments}
            onChange={handleChange}
            placeholder="Your message"
          />
          {contactFormData.submitState.isTouched && !fieldsValid.Comments && (
            <div className="field-validation-error">
              <div className="pl-3 text-red">Comments is required</div>
            </div>
          )}
          <div className="mt-[25px] md:mt-3 xl:w-3/4">
            <PopUpThanks
              name="Request info"
              buttonWidthFull={true}
              isBlogButton={true}
              buttonIsLoading={contactFormData.submitState.submitting}
              popupOpen={
                !contactFormData.submitState.isHeaderFormActive &&
                contactFormData.submitState.isSubmitted
              }
              closePopup={onClosingPopup}
            />
            <div className="mt-[16px] md:mt-3 text-11-14-0.3">
              I agree to be contacted by Condo Black Book LLC via call, email,
              and text. To opt-out, you can reply 'stop' at any time or click
              the unsubscribe link in the emails. Message and data rates may
              apply.{' '}
              <Link
                target="_blank"
                href="/terms-of-use"
                className="text-gold-darker"
                passHref
              >
                Terms of Use
              </Link>{' '}
              and{' '}
              <Link
                target="_blank"
                href="/privacy-policy/"
                className="text-gold-darker"
                passHref
              >
                Privacy Policy
              </Link>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default ContactUs;
